import { useQuery } from '@tanstack/react-query';

import {
  payCalendarsService,
  individualsService,
  FetchPayCalendarsRequest,
  useToast,
} from '@aslanpay/portal-common-lib';

import { PayCalendarsPage } from 'app/components/UI/Table/Table.types';

const useFetchPayCalendarsPage = ({
  ids,
  status,
  payFrequency,
  payday,
  payType,
  companyId,
  friendlyId,
  startDateBefore,
  startDateAfter,
  query,
  sortBy,
  sort,
  page,
  size,
}: FetchPayCalendarsRequest) => {
  const toast = useToast();

  return useQuery<PayCalendarsPage, Error>(
    [
      'fetchPayCalendarsPage',
      ids,
      status,
      payFrequency,
      payday,
      payType,
      companyId,
      friendlyId,
      startDateBefore,
      startDateAfter,
      query,
      sortBy,
      sort,
      page,
      size,
    ],
    async () => {
      const payCalendars = await payCalendarsService.fetchPayCalendars({
        ids,
        status,
        payFrequency,
        payday,
        payType,
        companyId,
        friendlyId,
        startDateBefore,
        startDateAfter,
        query,
        sortBy,
        sort,
        page,
        size,
      });

      const employees = await individualsService.fetchIndividuals({
        role: ['EMPLOYEE'],
        payCalendarId: payCalendars.list.map((payCalendar) => payCalendar.id),
        page: 0,
        size: 999999,
      });

      const payCalendarsList = payCalendars.list.map((payCalendar) => {
        const employeesCount = employees.list.reduce((acc, employee) => {
          if (employee.payCalendarId === payCalendar.id) {
            return acc + 1;
          }

          return acc;
        }, 0);

        const startDate = `${payCalendar.payCycles[0].startDate} to ${
          payCalendar.payCycles[payCalendar.payCycles.length - 1].endDate
        }`;

        return { ...payCalendar, employees: employeesCount, startDate };
      });

      return { ...payCalendars, list: payCalendarsList };
    },
    {
      onError: () => {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Something went wrong',
          description: `Sorry, we failed to fetch the pay calendars. Please try again.`,
        });
      },
    }
  );
};

export default useFetchPayCalendarsPage;
